import { patchState, signalStore, withMethods, withProps, withState } from '@ngrx/signals';
import { AccordionItem, FAQResponse } from '@mwe/models';
import { inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ENVIRONMENT_SERVICE_TOKEN } from '@mwe/constants';
import type { EnvironmentService } from '../environment/environment.service';
import { catchError, of, tap } from 'rxjs';
import { LoggingService } from '../logging/logging.service';
import { isArrayWithMinOneItem } from '@mwe/utils';
import { map } from 'rxjs/operators';

const initialState = {
  faqs: undefined as AccordionItem[],
};

export type FaqStoreState = typeof initialState;

export const FaqStore = signalStore(
  {
    providedIn: 'root',
  },
  withProps(() => ({
    _httpClient: inject(HttpClient),
    _environmentService: inject<EnvironmentService>(ENVIRONMENT_SERVICE_TOKEN),
    _loggingService: inject(LoggingService),
  })),
  withState(initialState),
  withMethods(store => {
    const updateState = (updates: Partial<FaqStoreState>) => {
      patchState(store, { ...updates });
    };

    const loadFaqs = ({ processId, categories = [] }: { processId: string; categories?: string[] }) => {
      const loggingMessage = `Error loading FAQs in ${processId}`;
      const capitalizedCategories = categories.map(c =>
        c
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' '),
      );
      const params = new HttpParams().appendAll({ processId: processId, sparte: capitalizedCategories });

      updateState({ faqs: [] });
      store._httpClient
        .get<FAQResponse>(`${store._environmentService.getApiUrl()}api/faqs`, { params })
        .pipe(
          map(response => {
            if (typeof response === 'string') {
              return JSON.parse(response) as FAQResponse;
            }

            return response;
          }),
          tap(({ items }) => {
            if (!isArrayWithMinOneItem(items)) {
              store._loggingService.logError(new Error(`${loggingMessage}: No items found`));
            }
            updateState({ faqs: items });
          }),
          catchError(err => {
            store._loggingService.logError(err, loggingMessage);
            updateState({ faqs: [] });
            return of(err);
          }),
        )
        .subscribe();
    };

    return {
      updateState,
      loadFaqs,
    };
  }),
);
